import React, { useEffect, useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { STATUS_COLOUR } from '../utils/evidenceStatus';

let geometryPromise;
function loadGeometry() {
  if (!geometryPromise)
    geometryPromise = fetch('/indonesia-provinces.geojson')
      .then((r) => {
        if (!r.ok) throw new Error('Province boundaries could not be loaded');
        return r.json();
      })
      .catch((error) => {
        geometryPromise = null;
        throw error;
      });
  return geometryPromise;
}

export default function IndonesiaChoropleth({
  values = [],
  metric = 'Conversations',
  onSelect,
  selected,
  palette = 'volume',
  height = 390,
  showTable = height > 300,
  // Provinces with fewer conversations than this are drawn grey (not enough evidence)
  // instead of on the colour scale, matching the heatmap's status rule.
  minCount = 0,
}) {
  const [geometry, setGeometry] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    loadGeometry()
      .then((g) => live && setGeometry(g))
      .catch(() => live && setError('Map boundaries unavailable.'));
    return () => {
      live = false;
    };
  }, []);
  const entries = useMemo(
    () => Object.fromEntries(values.map((x) => [String(x.province || '').toLowerCase(), x])),
    [values],
  );
  if (error) return <div className="decision-empty">{error}</div>;
  if (!geometry) return <div className="decision-empty">Loading province map…</div>;
  // Boundaries are keyed by the Indonesian name; conversation data uses the English
  // name, which each feature carries as name_en.
  const names = geometry.features.map((f) => f.properties.name);
  const english = Object.fromEntries(
    geometry.features.map((f) => [f.properties.name, f.properties.name_en || f.properties.name]),
  );
  const canonicalProvince = (name) => english[name] || name;
  const mapped = names.map((name) => entries[canonicalProvince(name).toLowerCase()]);
  const enough = (x) => Number(x.count || 0) >= minCount;
  const scaled = mapped.map((x) => (x && enough(x) ? x : null));
  const thin = mapped.map((x) => (x && !enough(x) ? x : null));
  const known = scaled.filter(Boolean).map((x) => Number(x.value || 0));
  const selectedValue = selected ? entries[String(selected).toLowerCase()] : null;
  const tableRows = [...values]
    .sort((a, b) =>
      a.province === selected
        ? -1
        : b.province === selected
          ? 1
          : Number(b.count || 0) - Number(a.count || 0),
    )
    .slice(0, 12);
  const abs = Math.max(1, ...known.map(Math.abs));
  const positiveNegative = palette === 'sentiment';
  const colorscale = positiveNegative
    ? [
        [0, '#c75b4b'],
        [0.5, '#eee9df'],
        [1, '#27805a'],
      ]
    : [
        [0, '#e6efe5'],
        [0.5, '#84bc9b'],
        [1, '#1c6b55'],
      ];
  const data = [
    {
      type: 'choropleth',
      geojson: geometry,
      featureidkey: 'properties.name',
      locations: names,
      z: names.map(() => 0),
      zmin: 0,
      zmax: 1,
      colorscale: [
        [0, '#e8e5df'],
        [1, '#e8e5df'],
      ],
      showscale: false,
      marker: { line: { color: '#fff', width: 0.5 } },
      hovertemplate: '%{location}<br>No recorded data<extra></extra>',
      showlegend: false,
    },
    {
      type: 'choropleth',
      geojson: geometry,
      featureidkey: 'properties.name',
      locations: names.filter((_, i) => thin[i]),
      z: thin.filter(Boolean).map(() => 0),
      zmin: 0,
      zmax: 1,
      colorscale: [
        [0, STATUS_COLOUR.grey],
        [1, STATUS_COLOUR.grey],
      ],
      showscale: false,
      marker: { line: { color: '#fff', width: 0.7 } },
      text: thin
        .filter(Boolean)
        .map(
          (x) =>
            `${canonicalProvince(x.province)}<br>${x.count} conversations<br>Not enough evidence (fewer than ${minCount})`,
        ),
      hovertemplate: '%{text}<extra></extra>',
      showlegend: false,
    },
    {
      type: 'choropleth',
      geojson: geometry,
      featureidkey: 'properties.name',
      locations: names.filter((_, i) => scaled[i]),
      z: scaled.filter(Boolean).map((x) => Number(x.value || 0)),
      zmin: positiveNegative ? -abs : 0,
      zmax: abs,
      colorscale,
      showscale: true,
      colorbar: { title: { text: metric, side: 'right' }, thickness: 10, len: 0.55 },
      marker: { line: { color: '#fff', width: 0.7 } },
      text: scaled
        .filter(Boolean)
        .map(
          (x) =>
            `${canonicalProvince(x.province)}<br>${metric}: ${x.value}${x.count != null ? `<br>${x.count} conversations` : ''}`,
        ),
      hovertemplate: '%{text}<extra></extra>',
      showlegend: false,
    },
  ];
  return (
    <div className={`indonesia-map ${showTable ? 'with-table' : ''}`}>
      <div className="map-graphic">
        <Plot
          data={data}
          layout={{
            height,
            autosize: true,
            margin: { l: 0, r: 0, t: 0, b: 0 },
            paper_bgcolor: 'transparent',
            geo: {
              visible: false,
              projection: { type: 'mercator', scale: 1.3 },
              lonaxis: { range: [94, 142] },
              lataxis: { range: [-12, 9] },
              bgcolor: 'rgba(0,0,0,0)',
            },
          }}
          config={{ displayModeBar: false, responsive: true }}
          useResizeHandler
          style={{ width: '100%' }}
          onClick={(event) => {
            const name = event.points?.[0]?.location;
            if (name) onSelect?.(canonicalProvince(name));
          }}
        />
        <div className="map-caption">
          Select a province to narrow the view. Grey means no matching conversations
          {minCount > 1 ? `, or fewer than ${minCount} (not enough evidence)` : ''}
          {selected ? ` · Selected: ${selected}` : ''}.
        </div>
      </div>
      {showTable && (
        <div className="map-province-table">
          <h4>{selected ? `${selected}: recorded evidence` : 'Provinces with recorded data'}</h4>
          {selected && (
            <>
              <button className="map-reset" onClick={() => onSelect?.('')}>
                ← All provinces
              </button>
              {selectedValue ? (
                <div className="map-selected-detail">
                  <div>
                    <span>{metric}</span>
                    <strong>
                      {selectedValue.value}
                      {palette === 'sentiment' ? '%' : ''}
                    </strong>
                  </div>
                  <div>
                    <span>Conversations</span>
                    <strong>{selectedValue.count ?? '—'}</strong>
                  </div>
                  {palette === 'sentiment' ? (
                    <>
                      <div>
                        <span>Positive assessments</span>
                        <strong>{selectedValue.positive ?? 0}</strong>
                      </div>
                      <div>
                        <span>Negative assessments</span>
                        <strong>{selectedValue.negative ?? 0}</strong>
                      </div>
                      <div>
                        <span>Mixed assessments</span>
                        <strong>{selectedValue.mixed ?? 0}</strong>
                      </div>
                    </>
                  ) : (
                    <div>
                      <span>Issue mentions</span>
                      <strong>{selectedValue.issues ?? 0}</strong>
                    </div>
                  )}
                </div>
              ) : (
                <p>No recorded evidence for this province in the selected scope.</p>
              )}
              <h5>Compare with other provinces</h5>
            </>
          )}
          <div className="map-province-head">
            <span>Province</span>
            <span>{metric}</span>
            <span>Conversations</span>
          </div>
          {tableRows
            .filter((row) => row.province !== selected)
            .map((row) => (
              <button key={row.province} onClick={() => onSelect?.(row.province)}>
                <span>{row.province}</span>
                <strong>
                  {row.value}
                  {palette === 'sentiment' ? '%' : ''}
                </strong>
                <small>{row.count ?? '—'}</small>
              </button>
            ))}
          {!tableRows.length && <p>No province-level records in this selection.</p>}
        </div>
      )}
    </div>
  );
}
